import type {ReactElement} from 'react';
import type {EntityThumbnailWithPayload, StreamedEntityThumbnailTileProps} from './streamed-entity-thumbnail-tile.types';
import {ENTITY_THUMBNAIL_TILE_CONFIGS, MIN_STREAMED_THUMBNAILS_TO_RENDER} from './streamed-entity-thumbnail-tile.types';
import {EntityThumbnailTile} from '@Components/ai-chat-window/components/entity-thumbnail-tile/entity-thumbnail-tile';
import {EntityThumbnailTileVariant} from '@Components/ai-chat-window/components/entity-thumbnail-tile/entity-thumbnail-tile.types';
import {getEntityCardAction} from '@Components/ai-chat-window/components/entity-card/actions/entity-card-action-registry';
import {useClickEntity, useEntityCardActionContext} from '@Components/ai-chat-window/components/entity-card/entity-card.hooks';
import {IconType} from '@Components/icon-v2/icon.types';

/** A stream-sent tile of entity thumbnails headed by the agent's heading, whose subtitle follows its entity type and whose clicks run that type's entity action. */
export function StreamedEntityThumbnailTile({entityType, heading, items, viewMoreUrl}: StreamedEntityThumbnailTileProps): ReactElement | null {
  const config = ENTITY_THUMBNAIL_TILE_CONFIGS[entityType];
  const action = getEntityCardAction(entityType);
  const clickEntity = useClickEntity(useEntityCardActionContext());

  const thumbnails: EntityThumbnailWithPayload[] = items.map((item) => ({
    id: item.entityRef,
    previewUrl: item.thumbnailUrl,
    altText: item.title,
    entity: item.entity,
  }));

  const openViewMore = (): void => {
    window.open(viewMoreUrl, '_blank');
  };

  const clickThumbnail = (thumbnail: EntityThumbnailWithPayload): void => {
    if (!action) {
      return;
    }

    clickEntity(action, thumbnail.entity);
  };

  return (
    <EntityThumbnailTile
      title={heading}
      subtitle={window.i18next.t(config.subtitleKey)}
      subtitleIcon={config.subtitleIcon}
      mobileSubtitle={window.i18next.t(config.mobileSubtitleKey)}
      illustrationUrl={null}
      thumbnails={thumbnails}
      minThumbnailsToRender={MIN_STREAMED_THUMBNAILS_TO_RENDER}
      hoverAction={{icon: config.hoverIcon, iconType: IconType.NONE, label: window.i18next.t(config.hoverLabelKey)}}
      viewMoreAction={{label: window.i18next.t(config.viewMoreLabelKey), icon: config.viewMoreIcon, onClick: openViewMore}}
      onThumbnailClick={clickThumbnail}
      variant={EntityThumbnailTileVariant.FULL}
    />
  );
}
